/**
 * Mercure Utility Functions
 *
 * Provides helpers for:
 * - resolveTopic: Replace :param placeholders with actual values
 * - parseNotification: Convert API Platform format to app format
 * - parseDocument: Convert API Platform document format (for future use)
 */

/**
 * Resolve topic pattern with params
 * @param {string} pattern - Topic with :param placeholders (e.g., "/api/users/:userId")
 * @param {Object} params - Key-value pairs (e.g., { userId: 2 })
 * @returns {string} Resolved topic (e.g., "/api/users/2")
 *
 * Example:
 *   resolveTopic("/api/users/:userId/permissions", { userId: 5 })
 *   // → "/api/users/5/permissions"
 */
export const resolveTopic = (pattern, params) => {
  if (!pattern || !params) return pattern;
  return pattern.replace(/:(\w+)/g, (_, key) => {
    return params[key] !== undefined ? params[key] : `:${key}`;
  });
};

/**
 * Parse API Platform notification to app format
 * @param {Object} data - Raw Mercure event data
 * @returns {Object} Parsed notification
 *
 * Example input:
 *   { id: "/api/notifications/1", title: "New message", message: "Hello",
 *     type: "info", dateCreation: "2024-01-15T10:00:00Z", readAt: null }
 *
 * Example output:
 *   { id: "/api/notifications/1", title: "New message", message: "Hello",
 *     type: "info", timestamp: "2024-01-15T10:00:00Z", read: false }
 */
export const parseNotification = (data) => {
  if (!data) return null;

  return {
    id: data.id || data["@id"],
    title: data.title,
    message: data.message,
    type: data.type || "info",
    timestamp: data.dateCreation,
    read: data.readAt !== null,
  };
};

/**
 * Parse API Platform document to app format (for future document subscriptions)
 * @param {Object} data - Raw Mercure event data
 * @returns {Object} Parsed document
 */
export const parseDocument = (data) => {
  if (!data) return null;

  return {
    id: data.id || data["@id"],
    title: data.title,
    type: data.type,
    filename: data.filename,
    mimeType: data.mimeType,
    size: data.size,
    dateCreation: data.dateCreation,
  };
};

export default {
  resolveTopic,
  parseNotification,
  parseDocument,
};
